import { atom, atomStore, atomWithDefault, RESET, withEqualityCheckedSet } from '@figma/jotai'
import { loafOperationTracker, TrackedOperation } from '@figma/loaf-operation-tracker'

import { type ConsentRegion, getConsentRegion } from 'lib/initial_options'

import { getCookieStorage } from './get_cookie_storage'

const ONE_YEAR_DURATION_SEC = 365 * 24 * 60 * 60

// Keep in sync with the marketing website
export const COOKIE_PREFERENCE_COOKIE_NAME = 'pref'

export type CookieValue = {
  t: ConsentRegion
  f: boolean
  a: boolean
  m: boolean
}

export enum CookieExistingPreferences {
  UNLOADED = 'unloaded',
  NO = 'no',
  YES = 'yes',
}

function getCookieValueFromStorage() {
  return loafOperationTracker.trackLoafEntry(TrackedOperation.GetCookieValueFromStorage, () => {
    const storage = getCookieStorage()
    if (!storage) {
      return undefined
    } else {
      return storage.get(COOKIE_PREFERENCE_COOKIE_NAME) as CookieValue | null
    }
  })
}

async function getCookieValueFromStorageAsync(priority: SchedulerPriority = 'background') {
  try {
    return await scheduler.postTask(() => getCookieValueFromStorage(), { priority })
  } catch {
    return undefined
  }
}

function setCookieValueToStorage(cookieValue: CookieValue | null | undefined) {
  const storage = getCookieStorage()
  if (!storage) return

  const cookieOptions = { maxAge: ONE_YEAR_DURATION_SEC }
  storage.set(COOKIE_PREFERENCE_COOKIE_NAME, cookieValue, cookieOptions)
}

async function setCookieValueToStorageAsync(
  cookieValue: CookieValue | null | undefined,
  priority: SchedulerPriority = 'background',
) {
  try {
    await scheduler.postTask(() => setCookieValueToStorage(cookieValue), { priority })
  } catch {}
}

const INTERNAL_cookiePreferencesValueAtom = withEqualityCheckedSet(
  atomWithDefault<CookieValue | null | undefined>(() => getCookieValueFromStorage()),
)

export async function forceUpdateCookiePreferences() {
  atomStore.set(INTERNAL_cookiePreferencesValueAtom, await getCookieValueFromStorageAsync())
}

// Handle cookie changes from other tabs
window.addEventListener('focus', () => forceUpdateCookiePreferences())

export async function INTERNAL_setCookiePreferences(cookieValue?: CookieValue | null) {
  atomStore.set(INTERNAL_cookiePreferencesValueAtom, cookieValue)
  await setCookieValueToStorageAsync(cookieValue)
}

/**
 * For users in explicit or implicit consent regions with the ability to opt out of cookies,
 * set their cookie preference using the `pref` key in document.cookie
 * See https://paper.dropbox.com/doc/PRD-In-Product-Logged-Out-Cookie-Banner--BwqjhfkjB7Jxtk3bWrWRAImZAg-525pfgGBOOpTGXscYzziz
 * @param settings.consentRegion - the region this user is in
 * @param settings.cookiesEnabled - whether they have opted in or out of cookies
 */
export async function setCookiePreferences({
  consentRegion,
  cookiesEnabled,
}: {
  consentRegion: ConsentRegion
  cookiesEnabled: boolean
}) {
  const cookieValue: CookieValue = {
    t: consentRegion,
    f: cookiesEnabled,
    a: cookiesEnabled,
    m: cookiesEnabled,
  }
  await INTERNAL_setCookiePreferences(cookieValue)
}

export const cookieExistingPreferencesAtom = atom<CookieExistingPreferences>((get) => {
  switch (get(INTERNAL_cookiePreferencesValueAtom)) {
    case RESET:
    case undefined:
      return CookieExistingPreferences.UNLOADED
    case null:
      return CookieExistingPreferences.NO
    default:
      return CookieExistingPreferences.YES
  }
})

export const canUseCookieForAnalyticsAtom = atom<boolean>((get) => {
  const cookieValue = get(INTERNAL_cookiePreferencesValueAtom)
  switch (cookieValue) {
    case RESET:
    case undefined:
      return false
    case null:
      // https://docs.google.com/spreadsheets/d/13zQiVdRV7LwjgAXNTZQbYhRpHI0GKv9Vh53hy9XVZh0/edit
      return getConsentRegion() !== 'explicit'
    default:
      return cookieValue.a
  }
})

export const canUseCookieForMarketingAtom = atom<boolean>((get) => {
  const cookieValue = get(INTERNAL_cookiePreferencesValueAtom)
  switch (cookieValue) {
    case RESET:
    case undefined:
      return false
    case null:
      // https://docs.google.com/spreadsheets/d/13zQiVdRV7LwjgAXNTZQbYhRpHI0GKv9Vh53hy9XVZh0/edit
      return !getConsentRegion()
    default:
      return cookieValue.m
  }
})

export const canUseCookieForFunctionalAtom = atom<boolean>((get) => {
  const cookieValue = get(INTERNAL_cookiePreferencesValueAtom)
  switch (cookieValue) {
    case RESET:
    case undefined:
      return false
    case null:
      // https://docs.google.com/spreadsheets/d/13zQiVdRV7LwjgAXNTZQbYhRpHI0GKv9Vh53hy9XVZh0/edit
      return getConsentRegion() !== 'explicit'
    default:
      return cookieValue.f
  }
})

export const cookieBannerHeightAtom = atom<number>(0)
